/**
 * Utility class for sanitizing API payloads
 */
export class APISanitizerUtilities {
  
  /**
   * Sanitizes a payload by converting empty strings to null values
   * @param payload - The object to sanitize
   * @returns The sanitized object with empty strings converted to null
   */
  public static sanitizePayload<T>(payload: T): T {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    // Create a copy of the object to avoid mutating the original
    const sanitizedPayload = { ...payload } as any;

    // Iterate through each property of the object
    for (const key in sanitizedPayload) {
      if (sanitizedPayload.hasOwnProperty(key)) {
        const value = sanitizedPayload[key];
        
        // Check if the value is undefined and convert to null
        if (value === undefined) {
          sanitizedPayload[key] = null;
        }
        // Check if the property is a string type
        else if (typeof value === 'string') {
          // Convert empty strings to null
          if (value === '') {
            sanitizedPayload[key] = null;
          }
        }
        // Handle nested objects recursively (but preserve Date objects)
        else if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
          sanitizedPayload[key] = this.sanitizePayload(value);
        }
        // Handle arrays
        else if (Array.isArray(value)) {
          sanitizedPayload[key] = value.map(item => 
            item === undefined ? null :
            typeof item === 'string' && item === '' ? null :
            typeof item === 'object' && !(item instanceof Date) ? this.sanitizePayload(item) :
            item
          );
        }
      }
    }

    return sanitizedPayload;
  }
}
